import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box, Button, CircularProgress, Dialog, DialogActions, Slider, Tab, Tabs } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { BoxTypeMap } from '@mui/system';
import { Html5Qrcode } from 'html5-qrcode';
import { isEmpty } from 'lodash';
import { SyntheticEvent, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToastOptions, toast } from 'react-toastify';
import styled from 'styled-components';
import useResizeObserver from 'use-resize-observer';
import { getCameraTab, setCameraTab } from '../../redux/slices/app/appSlice';
import Skeleton from '../skeleton/Skeleton';
import { QrScanProps } from './interface';
import { findRearCamera, useQrScanner } from './useQrScanner';

const ScanContainer = styled(Box)`
  min-height: 150px;
  max-height: 350px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.palette.grey[200]};
  overflow: hidden;
  position: relative;
  touch-action: pan-y;
  video {
    height: auto;
    width: 100% !important;
  }
  svg {
    opacity: 0.3;
  }
  & .MuiSvgIcon-root {
    width: 100px;
    height: 100px;
  }
`;

const LoaderContainer = styled(Box)`
  position: relative;
  z-index: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ScanContainerIn = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['$width', '$height'].includes(prop),
})<OverridableComponent<BoxTypeMap> & { $width?: number; $height?: number }>`
  flex: auto;
  display: flex;
  z-index: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 900px;
  position: absolute !important;
  left: 50%;
  top: 0;
  margin-left: -450px;
  margin-top: ${(props) => (props.$height ? `-${(props.$height - 350) / 2}px` : `-70px`)};
  > div {
    width: 100%;
  }
`;

const toastOptions = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'dark',
} as ToastOptions;

const QrScan: React.FC<QrScanProps> = ({ open, onClose, checkScanResult }: QrScanProps) => {
  const dispatch = useDispatch();
  const tabValue = useSelector(getCameraTab);
  const tabValueRef = useRef<number>(tabValue);
  tabValueRef.current = tabValue;
  const devicesRef = useRef<{ id: string; label: string }[] | null | undefined>();
  const decodedRef = useRef(false);
  const { ref, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>();
  const readerRef = useRef<HTMLDivElement>(null);

  const { start, stop, capabilities, zoom, setZoom, readerBoxRef } = useQrScanner({
    readerId: 'reader',
    onDecode: async (decodedText) => {
      if (decodedRef.current) return;
      decodedRef.current = true;
      await stop();
      await checkScanResult(decodedText.split('/').reverse()[0]);
      onClose();
    },
  });

  const setCamera = async (deviceItem) => {
    const device = devicesRef.current?.[deviceItem];

    if (device) {
      try {
        await start(device.id);
      } catch (error) {
        toast.error(typeof error === 'string' ? error : (error as Error)?.message ?? 'Unable to start camera', toastOptions);
      }
    }
  };

  const handleChangeTab = (newValue, force?) => {
    const gotVideo = readerRef.current?.innerHTML !== '';
    if (gotVideo || force) {
      dispatch(setCameraTab(newValue));
      setCamera(newValue);
    }
  };

  useEffect(() => {
    const openScan = async () => {
      decodedRef.current = false;
      try {
        const devices = await Html5Qrcode.getCameras();
        devicesRef.current = devices;
        if (tabValueRef.current < 0) {
          const rear = findRearCamera(devices);
          const rearIndex = Math.max(
            devices.findIndex((device) => device.id === rear?.id),
            0,
          );
          handleChangeTab(rearIndex, true);
        } else {
          handleChangeTab(tabValueRef.current, true);
        }
      } catch {
        devicesRef.current = null;
        handleChangeTab(0, true);
      }
    };
    if (open) {
      openScan();
    }
  }, [open]); // eslint-disable-line

  const handleQrClose = async (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
    if (reason !== 'backdropClick') {
      await stop();
      onClose();
    }
  };

  useEffect(() => {
    if (!open) {
      stop();
      if (readerRef.current) {
        readerRef.current.innerHTML = '';
      }
    }
  }, [open, stop]);

  return (
    <Dialog open={open} fullWidth PaperProps={{ sx: { maxWidth: 600, margin: 1, width: '100%', maxHeight: 'unset' } }} onClose={handleQrClose}>
      <Tabs
        value={tabValue >= 0 && !isEmpty(devicesRef.current) ? tabValue : 0}
        onChange={(event, value) => handleChangeTab(value)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {devicesRef.current === undefined && <Tab label={<Skeleton />} />}
        {devicesRef.current === null && <Tab label="No Camera Access" />}
        {devicesRef.current && isEmpty(devicesRef.current) && <Tab label="No Camera" />}
        {devicesRef.current && !isEmpty(devicesRef.current) && devicesRef.current.map((device) => <Tab label={device.label} key={device.id} />)}
      </Tabs>
      <ScanContainer ref={readerBoxRef} sx={{ height }}>
        <LoaderContainer>
          <div>{devicesRef.current === null ? <VideocamOffOutlinedIcon /> : <VideocamOutlinedIcon />}</div>
          {devicesRef.current === undefined && (
            <div>
              <CircularProgress color="inherit" size={22} />
            </div>
          )}
        </LoaderContainer>
        <ScanContainerIn $width={width} $height={height} ref={ref}>
          <div id="reader" ref={readerRef} />
        </ScanContainerIn>
      </ScanContainer>
      {capabilities?.zoom && zoom !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 6, pt: 1 }}>
          <ZoomOutIcon fontSize="small" color="action" />
          <Slider
            size="small"
            value={zoom}
            onChange={(event, value) => setZoom(value as number)}
            min={capabilities.zoom.min}
            max={capabilities.zoom.max}
            step={capabilities.zoom.step || 0.1}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value.toFixed(1)}×`}
          />
          <ZoomInIcon fontSize="small" color="action" />
        </Box>
      )}
      <DialogActions sx={{ px: 6, pb: 2, pt: 0 }}>
        <Button onClick={handleQrClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QrScan;
